import type { MiraseeWelcomePageConfig } from 'server/types/pmts-overrides';

const TOKEN_REGEX = /\{\{\s*deferred_enrollment_date\s*\}\}/g;

// Render the date in UTC so that an input like "2026-05-04" always formats as
// "May 4, 2026" — regardless of where the server or client lives. Without an
// explicit timeZone, Intl.DateTimeFormat uses the runtime tz, which shifts the
// rendered day for any user west of UTC.
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});

function parseDate(value: Date | string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }
  // YYYY-MM-DD strings parse as UTC midnight (consistent across tz). For
  // datetime strings, defer to the platform parser.
  return new Date(value);
}

function formatDate(value: Date | string | undefined): string {
  const date = parseDate(value);
  if (!date) {
    return '';
  }
  return dateFormatter.format(date);
}

function substitute(value: string | undefined, replacement: string): string | undefined {
  if (!value) {
    return value;
  }
  if (!TOKEN_REGEX.test(value)) {
    TOKEN_REGEX.lastIndex = 0;
    return value;
  }
  TOKEN_REGEX.lastIndex = 0;
  return value.replace(TOKEN_REGEX, replacement);
}

export type WelcomeTokenContext = {
  parentDeferredDate?: Date | string;
  upsellDeferredDate?: Date | string;
};

export function resolveWelcomePageTokens(
  welcomePage: MiraseeWelcomePageConfig,
  ctx: WelcomeTokenContext,
): MiraseeWelcomePageConfig {
  const parentDate = formatDate(ctx.parentDeferredDate);
  const upsellDate = formatDate(ctx.upsellDeferredDate);

  return {
    ...welcomePage,
    headline: substitute(welcomePage.headline, parentDate) ?? welcomePage.headline,
    subtitle: substitute(welcomePage.subtitle, parentDate),
    bodyHtml: substitute(welcomePage.bodyHtml, parentDate),
    upsellBodyHtml: substitute(welcomePage.upsellBodyHtml, upsellDate),
    ctaText: substitute(welcomePage.ctaText, parentDate),
    videoCaption: substitute(welcomePage.videoCaption, parentDate),
    calendar: welcomePage.calendar
      ? {
          ...welcomePage.calendar,
          title: substitute(welcomePage.calendar.title, parentDate) ?? welcomePage.calendar.title,
          description: substitute(welcomePage.calendar.description, parentDate),
          location: substitute(welcomePage.calendar.location, parentDate),
        }
      : welcomePage.calendar,
    smsOptin: welcomePage.smsOptin
      ? {
          ...welcomePage.smsOptin,
          heading: substitute(welcomePage.smsOptin.heading, parentDate),
          description: substitute(welcomePage.smsOptin.description, parentDate),
          buttonText: substitute(welcomePage.smsOptin.buttonText, parentDate),
        }
      : welcomePage.smsOptin,
  };
}
